import React, { useState, useEffect, useRef } from 'react';

interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  controls?: boolean;
  className?: string;
  onError?: () => void;
  useProxy?: boolean;
  authRequired?: boolean;
  username?: string;
  password?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  autoPlay = true,
  muted = true,
  controls = true,
  className = '',
  onError,
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const lastRefreshRef = useRef<number>(0);

  // Auto-refresh iframe every 5 minutes to prevent timeout issues
  useEffect(() => {
    if (!src.includes('/stream.html')) return;

    const interval = setInterval(() => {
      const now = Date.now();
      // Only refresh if last refresh was more than 4 minutes ago
      if (now - lastRefreshRef.current > 4 * 60 * 1000) {
        console.log('🔄 Auto-refreshing video stream to prevent timeout');
        setRefreshKey(prev => prev + 1);
        lastRefreshRef.current = now;
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [src]);
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-black ${className}`}>
        <p className="text-white text-sm">No stream available</p>
      </div>
    );
  }

  // Backend provides complete player_url with JWT token
  // Example: http://192.168.2.108:1984/stream.html?src=camera1&mode=webrtc,mse,mp4&token=eyJ...
  // Or we construct it: http://192.168.2.108:1984/stream.html?src=camera1&mode=webrtc,mse,mp4
  
  // Check if this is a go2rtc web player URL (contains stream.html)
  if (src.includes('/stream.html') || src.includes('/stream/stream.html')) {
    console.log('📹 Loading go2rtc stream:', src.substring(0, 100) + '...');
    
    // Add CSS to ensure video inside iframe fits without zooming
    const iframeStyle = {
      width: '100%',
      height: '100%',
      border: 0,
      display: 'block',
      backgroundColor: '#000'
    };
    
    return (
      <iframe
        src={src}
        className={className}
        style={iframeStyle}
        title="Camera Stream"
        allow="autoplay; microphone; camera"
        sandbox="allow-same-origin allow-scripts allow-forms"
        key={`${src}-${refreshKey}`}
      />
    );
  }
  
  // Fallback: If we receive an API endpoint URL instead of player URL, try to parse it
  // This handles backward compatibility
  try {
    const url = new URL(src);
    const params = new URLSearchParams(url.search);
    const cameraSource = params.get('src');
    const token = params.get('token');
    const hostname = url.hostname;
    const port = url.port || '8000';
    
    if (cameraSource && hostname) {
      // Build authenticated go2rtc iframe URL
      const iframeUrl = `http://${hostname}:${port}/stream/stream.html?src=${cameraSource}&mode=webrtc,mse,mp4${token ? `&token=${token}` : ''}`;
      console.log('📹 Loading authenticated stream (parsed):', iframeUrl.substring(0, 80) + '...');
      
      return (
        <iframe
          src={iframeUrl}
          className={`w-full h-full border-0 bg-black ${className}`}
          title="Camera Stream"
          allow="autoplay; microphone; camera"
          sandbox="allow-same-origin allow-scripts allow-forms"
          key={`${iframeUrl}-${refreshKey}`}
        />
      );
    }
  } catch (e) {
    console.error('Failed to parse stream URL:', e);
  }

  return (
    <div className={`flex items-center justify-center bg-black ${className}`}>
      <p className="text-white text-sm">Invalid stream URL</p>
    </div>
  );
};

export default React.memo(VideoPlayer);
